import { type PrimitiveAtom, useAtom } from 'jotai'
import React from 'react'

import {
  type CustomerAttribute,
  type CustomerAttributeGroup,
  useSurveyQueryCustomerAttributeGroups,
} from '~/hooks/useSurveyQuery'

import {
  CustomerAttributeTreeViewBoxInner,
  type CustomerAttributeTreeViewBoxInnerProps,
} from './CustomerAttributeTreeViewBoxInner'

export type CustomerAttributeTreeViewBoxProps = Pick<
  CustomerAttributeTreeViewBoxInnerProps<
    CustomerAttributeGroup,
    CustomerAttribute
  >,
  'onSelectedCustomerAttributeIdsChange'
> & {
  customerAttributeIdsAtom: PrimitiveAtom<CustomerAttribute['id'][]>
  'data-testid'?: string
}

export const CustomerAttributeTreeViewBox = ({
  customerAttributeIdsAtom,
  'data-testid': dataTestId = 'components-features-customer-attribute-tree-view-box',
  onSelectedCustomerAttributeIdsChange,
}: CustomerAttributeTreeViewBoxProps) => {
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()

  const [customerAttributeIds, setCustomerAttributeIds] = useAtom(
    customerAttributeIdsAtom,
  )

  const handleSelectedCustomerAttributeIdsChange = React.useCallback<
    EH<
      CustomerAttributeTreeViewBoxInnerProps<
        CustomerAttributeGroup,
        CustomerAttribute
      >,
      'onSelectedCustomerAttributeIdsChange'
    >
  >(
    (customerAttributeIds) => {
      setCustomerAttributeIds(customerAttributeIds)

      onSelectedCustomerAttributeIdsChange?.(customerAttributeIds)
    },
    [onSelectedCustomerAttributeIdsChange, setCustomerAttributeIds],
  )

  return (
    <CustomerAttributeTreeViewBoxInner
      customerAttributeGroups={customerAttributeGroups}
      data-testid={dataTestId}
      selectedCustomerAttributeIds={customerAttributeIds}
      onSelectedCustomerAttributeIdsChange={
        handleSelectedCustomerAttributeIdsChange
      }
    />
  )
}
